import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import moment from "moment";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { DownloadForOffline } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import {
  themes,
  scrollTop,
  tableRowSx,
  defaultAllSelection,
  optionalAvathar,
  headerMessage,
} from "services/constants";
import DefaultMonthDateRangePicker from "pages/TeamZone/Approve/DateRangePicker";
import { useFetch } from "services/hooks/useFetch";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomAutoComplete } from "components/CustomComponents";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
const keys = ["user_name", "patrol_time", "patrol_date", "location"];

const SiteRounds = () => {
  const {
    domain,
    token,
    userDetails,
    globalSearchValue,
    topbarCompanyId,
    topbarCompanyName,
  } = useSelector((state) => state.tokenReducer);
  const { watch, setValue } = useForm({
    defaultValues: {
      employee_name: "All",
      employee_id: defaultAllSelection,
      location_name: "All",
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [patoralDetails, setPatrolDateails] = useState([]);
  const [masterDataDetails, setMasterDataDetails] = useState([]);
  const containerRef = useRef(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [triggerApi, setTriggerApi] = useState(false);
  const [locationListArray, setLocationListArray] = useState([]);
  const [userListArray, setUserListArray] = useState([]);
  const { result: userList } = useFetch(
    `${domain}list/employee?company_id=${topbarCompanyId}`,
  );
  const { result: locationList } = useFetch(
    `${domain}tracking/device-location-name/`,
  );
  const selectedUserId = watch("employee_id");
  const selectedLocationName = watch("location_name");
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);

  useEffect(() => {
    const getFilter = () => {
      const allUsers = {
        user_id: defaultAllSelection,
        label: "All",
        user_name: "All",
      };
      const allLocation = {
        label: "All",
        location_name: "All",
      };
      setUserListArray([allUsers, ...userList]);
      setLocationListArray([allLocation, ...locationList]);
    };
    getFilter();
  }, [locationList, locationListArray, userList, userListArray]);

  const patrolSummarizedHeader = [
    {
      name: "Employee Name",
      key: "user_name",
    },
    {
      name: "Employee Id",
      key: "employee_id",
    },
    {
      name: "Location",
      key: "location",
    },
    {
      name: "Date",
      key: "patrol_date",
    },
    {
      name: "Punch Time",
      key: "patrol_time",
    },
  ];

  useEffect(() => {
    const handlePatrol = async () => {
      try {
        setIsLoading(true);
        let searchStartDate = moment(startDate, "DD-MM-YYYY").format("YYYY-MM-DD");
        let searchEndDate = moment(endDate, "DD-MM-YYYY").format("YYYY-MM-DD");

        const response = await fetch(
          `${domain}tracking/patrol-logs/?user_id=${userDetails.id}&start_date=${searchStartDate}&end_date=${searchEndDate}&company_id=${topbarCompanyId}&selected_user_id=${selectedUserId}&location_name=${selectedLocationName}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          const flattened = res.flatMap((user) =>
            user.patrol_entries.map((entry) => ({
              ...entry,
              user_id: user.user_id,
              user_name: user.user_name,
              punch_count: user.punch_count,
              image_url: user.image_url,
              employee_id: user?.employee_id,
            })),
          );
          const sortedByDate = flattened.sort((a, b) => {
            const aDateTime = new Date(
              `${a.patrol_date.split("-").reverse().join("-")}T${a.patrol_time}`,
            );
            const bDateTime = new Date(
              `${b.patrol_date.split("-").reverse().join("-")}T${b.patrol_time}`,
            );
            return aDateTime - bDateTime;
          });
          setMasterDataDetails(sortedByDate);
        } else if (response.status === 409) {
          setPatrolDateails([]);
          setMasterDataDetails([]);
          sessionHandling();
        } else {
          setPatrolDateails([]);
          setMasterDataDetails([]);
          throw new Error('REQUEST_FAILED');
        }
      } catch (error) {
        console.error('[Index] operation failed:', error);
        setPatrolDateails([]);
        setMasterDataDetails([]);
        showToast("error", "Couldn't load site round details. Please refresh the page.");
      } finally {
        setIsLoading(false);
      }
    };
    if (
      triggerApi &&
      startDate !== "" &&
      endDate !== "" &&
      selectedUserId &&
      selectedLocationName
    ) {
      handlePatrol();
      setTriggerApi(false);
    }
  }, [
    domain,
    endDate,
    selectedLocationName,
    selectedUserId,
    startDate,
    token,
    topbarCompanyId,
    triggerApi,
    userDetails.id,
    watch,
  ]);

  useEffect(() => {
    if (topbarCompanyId) {
      setTriggerApi(true);
    }
  }, [topbarCompanyId]);

  useEffect(() => {
    const filteredResults = masterDataDetails?.filter((item) =>
      keys.some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearchValue.toString().toLowerCase()),
      ),
    );
    setPatrolDateails(filteredResults);
    setPage(0);
  }, [globalSearchValue, masterDataDetails]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const getPPatrolSummarizedReport = () => {
    let dataPreparation = [];
    patoralDetails?.forEach((element) => {
      let overAllData = {
        employeename: element?.user_name ?? "N/A",
        employee_id: element?.employee_id ?? "N/A",
        location: element?.location ?? "N/A",
        date: element?.patrol_date ?? "N/A",
        patrol_time: element?.patrol_time ?? "N/A",
      };
      dataPreparation = [...dataPreparation, overAllData];
    });
    const PatrolReport = ["Site Rounds Report"];
    const Space = [""];
    const Dates = ["Date"];
    const companyName = ["Company Name"];
    const GeneratedOn = ["Report Generated On"];
    PatrolReport.push("");
    Dates.push(`${startDate} to ${endDate}`);

    companyName.push(topbarCompanyName);
    GeneratedOn.push(moment(new Date()).format("DD/MM/YYYY, hh:mm a"));
    Space.push("");
    const items = dataPreparation;
    const TableHead = ["Employee Name"];
    TableHead.push("Employee Id");
    TableHead.push("Location");
    TableHead.push("Date");
    TableHead.push("Punch Time");
    const replacer = (key, value) => (value === null ? "" : value);
    const header = Object.keys(items[0]);
    let csv = items.map((row) =>
      header
        .map((fieldName) => JSON.stringify(row[fieldName], replacer))
        .join(","),
    );
    csv.unshift(TableHead.join(","));
    csv.unshift(Space.join(","));
    csv.unshift(GeneratedOn.join(","));
    csv.unshift(companyName.join(","));
    csv.unshift(Dates.join(","));
    csv.unshift(PatrolReport.join(","));
    csv = csv.join("\r\n");
    const ConvertData = csv;
    const element = document.createElement("a");
    const file = new Blob([ConvertData], {
      type: "text/plain",
    });
    element.href = URL.createObjectURL(file);
    element.download = "PatrolReport.csv";
    document.body.appendChild(element);
    element.click();
  };

  const handleStartDate = async (startDate, endDate) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setTriggerApi(true);
  };

  const hanldeLocationName = async (event, data) => {
    if (data) {
      setValue("location_name", data?.location_name);
      setValue("location_id", data?.id);
      setTriggerApi(true);
    } else {
      setValue("location_name", "");
      setValue("location_id", "");
      setTriggerApi(false);
    }
  };

  const hanldeEmployeeName = async (event, data) => {
    if (data) {
      setValue("employee_name", data);
      setValue("employee_id", data?.user_id);
      setTriggerApi(true);
      setShowAutoCompleteAvathar(false);
    } else {
      setTriggerApi(false);
      setShowAutoCompleteAvathar(true);
    }
  };

  return (
    <React.Fragment>
      {headerMessage(
        "Site Rounds",
        "Track employee patrol punches by location and date",
      )}
      <Mui.Grid container spacing={1} sx={{
  mt: 1.5,
  justifyContent: "flex-end",
}}>
        <Mui.Grid item xs={3} sm={6} md={3} lg={2}>
          <CustomAutoCompleteWithLogo
            dataList={userListArray?.map((obj) => ({
              ...obj,
              label: obj?.user_name,
              image: obj?.image_url,
            }))}
            selectedValue={watch("employee_name")}
            updateDataFunction={hanldeEmployeeName}
            placeHolder="Select the user name"
            label="Select User Name"
            showAvathar={showAutoCompleteAvathar}
            setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
          />
        </Mui.Grid>
        <Mui.Grid item xs={3} sm={6} md={3} lg={2}>
          <CustomAutoComplete
            dataList={locationListArray?.map((obj) => ({
              ...obj,
              label: obj?.location_name,
            }))}
            selectedValue={watch("location_name")}
            updateDataFunction={hanldeLocationName}
            placeHolder="Select the location name"
            label="Select Location Name"
          />
        </Mui.Grid>
        <Mui.Grid item xs={3} sm={6} md={3} lg={2}>
          <DefaultMonthDateRangePicker
            handleStartDate={handleStartDate}
            showToday={true}
            triggerDate={true}
          />
        </Mui.Grid>
        <Mui.Grid item xs={2} sm={2} md={2} lg={0.5} sx={{ mt: -0.6 }}>
          <Mui.IconButton
            onClick={() => {
              getPPatrolSummarizedReport();
            }}
            sx={{
              backgroundColor: themes.primary,
              "&:hover": { backgroundColor: themes.primary },
            }}
            disabled={patoralDetails?.length === 0 ? true : false}
          >
            <DownloadForOffline
              size="large"
              sx={{
                color:
                  patoralDetails?.length === 0
                    ? themes?.disable
                    : themes.downloadIconColor,
                fontSize: 40,
              }}
            />
          </Mui.IconButton>
        </Mui.Grid>
      </Mui.Grid>

      {isLoading ? (
        <Loader info="Loading..." />
      ) : (
        <>
          <Mui.Grid container>
            <Mui.TableContainer
              sx={{ mt: 2, width: "100%", maxHeight: 740, ml: 2, mr: 2 }}
            >
              <Mui.Table className="border">
                <Mui.TableHead
                  sx={{
                    backgroundColor: themes.primary,
                    position: "sticky",
                    top: -1,
                    zIndex: 10,
                  }}
                >
                  <Mui.TableRow sx={tableRowSx}>
                    {patrolSummarizedHeader.map((header) => (
                      <Mui.TableCell
                        sx={{ color: themes.headLine, fontWeight: "bold" }}
                      >
                        {header.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {patoralDetails?.length === 0 && !isLoading ? (
                    <TableEmptyState dataLength={masterDataDetails} />
                  ) : (
                    patoralDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((element) => (
                        <>
                          <Mui.TableRow sx={tableRowSx}>
                            {patrolSummarizedHeader.map((head) => (
                              <Mui.TableCell>
                                {head?.name === "Employee Name" ? (
                                  <Mui.Stack direction="Row">
                                    <Mui.Avatar
                                      sx={{ width: 23, height: 23 }}
                                      alt={element?.user_name
                                        ?.charAt(0)
                                        .toUpperCase()}
                                      src={
                                        element?.image_url ?? optionalAvathar
                                      }
                                    />
                                    &nbsp;
                                    {element?.user_name}
                                  </Mui.Stack>
                                ) : element[head.key] ? (
                                  element[head.key]
                                ) : (
                                  "N/A"
                                )}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>
                        </>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
          </Mui.Grid>
          {patoralDetails?.length > 25 && (
            <Mui.TablePagination
              className="custom-pagination"
              component="div"
              rowsPerPageOptions={[25, 50, 75, 100]}
              count={patoralDetails?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </>
      )}
    </React.Fragment>
  );
};

export default SiteRounds;
